import { Component } from '@theme/component';
import { onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Timeout ID for debounced resize handler
   * @type {number | null}
   */
  #resizeTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    window.removeEventListener('resize', this.#handleResize);
    clearTimeout(this.#resizeTimeout);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize handler that triggers overflow-list reflow after resizing stops
   */
  #handleResize = () => {
    clearTimeout(this.#resizeTimeout);
    this.#resizeTimeout = setTimeout(() => {
      this.refs.overflowMenu?.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
    }, 150);
  };

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (document.readyState === 'loading') return;
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      const immediateHeight = submenu.offsetHeight;

      // Reset carousels and recalculate dimensions after content-visibility changes.
      // Use double rAF — content-visibility: auto → visible needs two frames for
      // the browser to fully lay out the previously-skipped content.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          for (const slideshow of submenu.querySelectorAll('slideshow-component')) {
            const slides = slideshow.querySelector('slideshow-slides');

            if (slides?.swiper) {
              slides.swiper.slideTo(0, 0);
              slides.swiper.update();
            }
          }

          // Re-assert data-active — section hydration (morph.js) can strip it
          // between frames, reverting content-visibility to auto and capping offsetHeight
          submenu.dataset.active = '';

          // Re-measure submenu height after layout settles
          if (submenu.offsetHeight > 0 && this.#state.activeItem === item) {
            if (submenu.offsetHeight >= peakHeight) {
              peakHeight = submenu.offsetHeight;
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#setFullOpenHeaderHeight(submenu.offsetHeight);
            }
          }
        });
      });

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section
      // hydration) and Swiper initialization from dormant state (~200ms after
      // content-visibility changes). Both add/modify child elements.
      // Track peak measured height — Swiper init and morph.js can temporarily
      // collapse padding or revert content-visibility, producing a transient
      // smaller offsetHeight. Only apply heights >= the peak to avoid clipping.
      let peakHeight = immediateHeight;

      this.#submenuMutationObserver = new MutationObserver(() => {
        // Re-assert data-active — morph.js can strip it during section hydration
        if (!submenu.hasAttribute('data-active')) {
          submenu.dataset.active = '';
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            submenu.dataset.active = '';
            const mutHeight = submenu.offsetHeight;

            if (mutHeight > 0 && this.#state.activeItem === item) {
              if (mutHeight < peakHeight) return;

              peakHeight = mutHeight;
              this.headerComponent?.style.setProperty('--submenu-height', `${mutHeight}px`);
              this.#setFullOpenHeaderHeight(mutHeight);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true, attributes: true});

      // Auto-disconnect after 1s to cover the slideshow.js dormant→active transition
      // (200ms debounce + Swiper init time + mega-menu-carousel configuration)
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 1000);

      // Monitor image loads for height changes
      const images = submenu.querySelectorAll('img');

      for (const img of images) {
        if (!img.complete) {
          img.addEventListener('load', () => {
            if (this.#state.activeItem !== item) return;
            const imgLoadHeight = submenu.offsetHeight;

            if (imgLoadHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${imgLoadHeight}px`);
              this.#setFullOpenHeaderHeight(imgLoadHeight);
            }
          }, { once: true });
        }
      }

      // Check if fonts change the height
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          if (this.#state.activeItem !== item) return;
          const postFontHeight = submenu.offsetHeight;

          if (postFontHeight !== immediateHeight && postFontHeight > 0) {
            this.headerComponent?.style.setProperty('--submenu-height', `${postFontHeight}px`);
            this.#setFullOpenHeaderHeight(postFontHeight);
          }
        });
      }

      // Delayed re-measure safety net — correct stale CSS vars if morph.js
      // caused a transient shrink that was applied before the peak guard
      for (const delay of [500, 1500]) {
        setTimeout(() => {
          if (this.#state.activeItem !== item) return;
          submenu.dataset.active = '';
          const delayedHeight = submenu.offsetHeight;
          const currentSubmenuVar = parseInt(this.headerComponent?.style.getPropertyValue('--submenu-height')) || 0;

          if (delayedHeight > 0 && delayedHeight !== currentSubmenuVar) {
            this.headerComponent?.style.setProperty('--submenu-height', `${delayedHeight}px`);
            this.#setFullOpenHeaderHeight(delayedHeight);
          }
        }, delay);
      }
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    // When blur fires with no relatedTarget (e.g. focus lost to body from buttons inside
    // carousels), keep the menu open if the pointer is still over the menu item
    const isBlurWithinHoveredMenu =
      event.type === 'blur' &&
      !event.relatedTarget &&
      this.#state.activeItem?.closest('.menu-list__list-item')?.matches(':hover');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu || isBlurWithinHoveredMenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + this.headerComponent.offsetHeight;

    this.headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
